import React, { useState, useEffect } from 'react';
import { User, Printer, X, CheckSquare, Square, Settings } from 'lucide-react';

const BadgeGeneratorSupemir = () => {
  const [etudiants, setEtudiants] = useState([]);
  const [etudiantsFiltres, setEtudiantsFiltres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [recherche, setRecherche] = useState('');
  const [filtreNiveau, setFiltreNiveau] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [autorisationIds, setAutorisationIds] = useState([]);

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('badgeAccessSupemir') === 'true';
  });
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [logoUrl, setLogoUrl] = useState(() => {
    return localStorage.getItem('supemirLogo') || '/images/logo-supemir.png';
  });

  const [anneeScolaire, setAnneeScolaire] = useState(() => {
    return localStorage.getItem('supemirAnneeScolaire') || '2025-2026';
  });

  const [showConfig, setShowConfig] = useState(false);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    const correctPassword = 'supemir2025';

    if (passwordInput === correctPassword) {
      setIsAuthenticated(true);
      sessionStorage.setItem('badgeAccessSupemir', 'true');
      setPasswordError('');
    } else {
      setPasswordError('❌ Mot de passe incorrect');
      setPasswordInput('');
    }
  };

  useEffect(() => {
    fetchEtudiants();
  }, []);

  useEffect(() => {
    filtrerEtudiants();
  }, [etudiants, recherche, filtreNiveau]);

  const fetchEtudiants = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Vous devez être connecté');
      }

      const response = await fetch('    https://vmi1977988.contaboserver.net/api2/etudiant-supemir', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des étudiants');
      }

      const data = await response.json();

      // 🔥 Préfixer les URLs des images avec le serveur backend
      const etudiantsAvecImages = data.map(etudiant => ({
        ...etudiant,
        image: etudiant.image && etudiant.image.trim() !== ''
          ? `     https://vmi1977988.contaboserver.net${etudiant.image}`
          : ''
      }));

      setEtudiants(etudiantsAvecImages);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtrerEtudiants = () => {
    let resultats = etudiants;

    if (recherche) {
      resultats = resultats.filter(e =>
        (e.nomComplet && e.nomComplet.toLowerCase().includes(recherche.toLowerCase())) ||
        (e.codeMassar && e.codeMassar.toLowerCase().includes(recherche.toLowerCase())) ||
        (e.cin && e.cin.toLowerCase().includes(recherche.toLowerCase())) ||
        (e.email && e.email.toLowerCase().includes(recherche.toLowerCase()))
      );
    }

    if (filtreNiveau) {
      resultats = resultats.filter(e => e.niveau === parseInt(filtreNiveau));
    }

    setEtudiantsFiltres(resultats);
  };

  const niveauxUniques = [...new Set(etudiants.map(e => e.niveau).filter(Boolean))].sort((a, b) => a - b);

  const handleSelectAll = () => {
    setSelectedIds(etudiantsFiltres.map(e => e._id));
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleToggleAutorisation = (id) => {
    setAutorisationIds(prev =>
      prev.includes(id)
        ? prev.filter(autoId => autoId !== id)
        : [...prev, id]
    );
  };

  const handlePrint = () => {
    if (selectedIds.length === 0) {
      alert('Veuillez sélectionner au moins une carte à imprimer');
      return;
    }
    window.print();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/';
  };

  const etudiantsAfficher = etudiantsFiltres.filter(e => selectedIds.includes(e._id));

  const getFormationDisplay = (etudiant) => {
    if (etudiant.specialiteIngenieur) {
      return etudiant.specialiteIngenieur;
    } else if (etudiant.specialiteLicencePro) {
      return etudiant.specialiteLicencePro;
    } else if (etudiant.specialiteMasterPro) {
      return etudiant.specialiteMasterPro;
    } else if (etudiant.cycle) {
      return etudiant.cycle;
    } else if (etudiant.typeFormation) {
      const labels = {
        'CYCLE_INGENIEUR': 'Cycle Ingénieur',
        'LICENCE_PRO': 'Licence Pro',
        'MASTER_PRO': 'Master Pro',
        'MASI': 'MASI',
        'IRM': 'IRM'
      };
      return labels[etudiant.typeFormation] || etudiant.typeFormation;
    }
    return 'Formation';
  };

  if (!isAuthenticated) {
    return (
      <div style={styles.container}>
        <div style={styles.mainContent}>
          <div style={styles.passwordContainer}>
            <div style={styles.passwordBox}>
              <div style={styles.lockIcon}>🔐</div>
              <h2 style={styles.passwordTitle}>Accès Sécurisé SUPEMIR</h2>
              <p style={styles.passwordSubtitle}>
                Veuillez entrer le mot de passe pour accéder aux cartes étudiants
              </p>

              <form onSubmit={handlePasswordSubmit} style={styles.passwordForm}>
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => {
                    setPasswordInput(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder="Mot de passe"
                  style={styles.passwordInput}
                  autoFocus
                />

                {passwordError && (
                  <p style={styles.passwordErrorText}>{passwordError}</p>
                )}

                <button type="submit" style={styles.passwordButton}>
                  Accéder aux cartes
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.mainContent}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Chargement des étudiants SUPEMIR...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.mainContent}>
          <div style={styles.loadingContainer}>
            <p style={{ color: '#ef4444', fontSize: '18px', fontWeight: '600' }}>
              ❌ {error}
            </p>
            {error.includes('connecté') || error.includes('Session') ? (
              <button onClick={handleLogout} style={styles.btnRetry}>
                Se reconnecter
              </button>
            ) : (
              <button onClick={fetchEtudiants} style={styles.btnRetry}>
                Réessayer
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.mainContent}>
        <div style={styles.header} className="no-print">
          <div style={styles.headerContent}>
            <h1 style={styles.title}>🎓 Cartes Étudiants SUPEMIR</h1>
            <p style={styles.subtitle}>
              {selectedIds.length} carte(s) sélectionnée(s) sur {etudiantsFiltres.length}
              {autorisationIds.length > 0 && ` • ${autorisationIds.length} avec autorisation de sortie`}
            </p>
          </div>
        </div>

        <div style={styles.toolbar} className="no-print">
          <div style={styles.toolbarContent}>
            <div style={styles.filtresRow}>
              <div style={styles.filtreGroupe}>
                <input
                  type="text"
                  placeholder="🔍 Rechercher par nom, email, CIN ou code Massar..."
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                  style={styles.inputRecherche}
                />
              </div>

              <div style={styles.filtreGroupe}>
                <select
                  value={filtreNiveau}
                  onChange={(e) => setFiltreNiveau(e.target.value)}
                  style={styles.selectFiltre}
                >
                  <option value="">Tous les niveaux</option>
                  {niveauxUniques.map(niveau => (
                    <option key={niveau} value={niveau}>Niveau {niveau}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.actions}>
              <button
                onClick={() => setShowConfig(true)}
                style={styles.btnConfig}
              >
                <Settings size={18} />
                Configuration
              </button>

              <button
                onClick={handleSelectAll}
                style={styles.btnSelect}
                disabled={etudiantsFiltres.length === 0}
              >
                Tout sélectionner
              </button>

              {selectedIds.length > 0 && (
                <button
                  onClick={handleDeselectAll}
                  style={styles.btnDeselect}
                >
                  <X size={18} />
                  Désélectionner ({selectedIds.length})
                </button>
              )}

              <button
                onClick={handlePrint}
                style={styles.btnPrint}
                disabled={selectedIds.length === 0}
              >
                <Printer size={18} />
                Imprimer ({selectedIds.length})
              </button>
            </div>
          </div>
        </div>

        <div style={styles.contentArea}>
          <div className="screen-grid no-print" style={styles.grid}>
            {etudiantsFiltres.map(etudiant => (
              <div key={etudiant._id} style={styles.cardWrapper}>
                <div style={styles.checkboxContainer}>
                  <div
                    style={styles.checkbox}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleSelect(etudiant._id);
                    }}
                    title="Sélectionner pour impression"
                  >
                    {selectedIds.includes(etudiant._id) ? (
                      <CheckSquare size={24} color="#0066cc" />
                    ) : (
                      <Square size={24} color="#9ca3af" />
                    )}
                  </div>

                  <button
                    style={{
                      ...styles.btnAutorisation,
                      ...(autorisationIds.includes(etudiant._id) ? styles.btnAutorisationActive : {})
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleAutorisation(etudiant._id);
                    }}
                    title="Activer l'autorisation de sortie"
                  >
                    {autorisationIds.includes(etudiant._id) ? '🔴 Sortie' : '⚪ Sortie'}
                  </button>
                </div>

                <StudentBadgeSupemir
                  etudiant={etudiant}
                  logoUrl={logoUrl}
                  anneeScolaire={anneeScolaire}
                  formationDisplay={getFormationDisplay(etudiant)}
                  showAutorisation={autorisationIds.includes(etudiant._id)}
                />
              </div>
            ))}
          </div>

          <div className="print-grid" style={{ display: 'none' }}>
            {etudiantsAfficher.map(etudiant => (
              <div key={etudiant._id}>
                <StudentBadgeSupemir
                  etudiant={etudiant}
                  logoUrl={logoUrl}
                  anneeScolaire={anneeScolaire}
                  formationDisplay={getFormationDisplay(etudiant)}
                  showAutorisation={autorisationIds.includes(etudiant._id)}
                />
              </div>
            ))}
          </div>

          {etudiantsFiltres.length === 0 && (
            <div style={styles.emptyState}>
              <User size={64} color="#cbd5e1" />
              <p style={styles.emptyText}>Aucun étudiant trouvé</p>
            </div>
          )}
        </div>
      </div>

      {showConfig && (
        <ConfigModal
          logoUrl={logoUrl}
          setLogoUrl={setLogoUrl}
          anneeScolaire={anneeScolaire}
          setAnneeScolaire={setAnneeScolaire}
          showConfig={showConfig}
          setShowConfig={setShowConfig}
        />
      )}

      <style>{`
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }

        @media screen {
          .print-grid {
            display: none !important;
          }
        }

        @media print {
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .no-print {
            display: none !important;
          }

          .screen-grid {
            display: none !important;
          }

          .print-grid {
            display: flex !important;
            flex-direction: column;
            align-items: center;
            gap: 20mm;
            padding: 15mm;
          }

          .print-grid > div {
            page-break-inside: avoid;
          }

          .print-grid > div:nth-child(3n) {
            page-break-after: always;
          }

          @page {
            size: A4 portrait;
            margin: 15mm;
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const StudentBadgeSupemir = ({ etudiant, logoUrl, anneeScolaire, formationDisplay, showAutorisation = false }) => {
  const [qrDataUrl, setQrDataUrl] = useState('');

  const nomComplet = etudiant.nomComplet || '';

  useEffect(() => {
    import('qrcode').then(QRCode => {
      const baseUrl = window.location.origin;
      const qrData = `${baseUrl}/etudiant-supemir/${etudiant._id}`;

      QRCode.toDataURL(qrData, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H'
      })
        .then(url => {
          setQrDataUrl(url);
        })
        .catch(err => {
          console.error('Erreur QR code:', err);
        });
    });
  }, [etudiant._id]);

  return (
    <div style={badgeStyles.wrapper}>
      <div style={badgeStyles.card}>
        <div style={badgeStyles.header}>
          <div style={badgeStyles.logoContainer}>
            {logoUrl ? (
              <img src={logoUrl} alt="Logo SUPEMIR" style={badgeStyles.logo} />
            ) : (
              <div style={badgeStyles.logoPlaceholder}>
                <span style={badgeStyles.logoText}>SUPEMIR</span>
              </div>
            )}
          </div>

          <div style={badgeStyles.headerText}>
            <p style={badgeStyles.cardType}>CARTE ÉTUDIANT</p>
            <p style={badgeStyles.cardSubtype}>ÉCOLE SUPÉRIEURE</p>
            <p style={badgeStyles.cardYear}>{anneeScolaire}</p>
          </div>

          <div style={badgeStyles.photoContainer}>
            {etudiant.image ? (
              <img
                src={etudiant.image}
                alt={nomComplet}
                style={badgeStyles.photo}
              />
            ) : (
              <div style={badgeStyles.photoPlaceholder}>
                <User size={34} color="#ffffff" strokeWidth={1.5} />
              </div>
            )}
          </div>
        </div>

        <div style={badgeStyles.body}>
          <div style={badgeStyles.contentGrid}>
            <div style={badgeStyles.infoSection}>
              <div style={badgeStyles.infoRowName}>
                <span style={badgeStyles.label}>NOM COMPLET:</span>
                <span style={{
                  ...badgeStyles.valueName,
                  fontSize: nomComplet.length > 20 ? '14px' : '15px'
                }}>
                  {nomComplet.toUpperCase()}
                </span>
              </div>

              <div style={badgeStyles.infoRow}>
                <span style={badgeStyles.label}>FORMATION:</span>
                <span style={badgeStyles.valueHighlight}>
                  {formationDisplay}
                </span>
              </div>

              {etudiant.codeMassar && (
                <div style={badgeStyles.infoRowMassar}>
                  <span style={badgeStyles.label}>CODE MASSAR:</span>
                  <span style={badgeStyles.valueMassar}>
                    {etudiant.codeMassar}
                  </span>
                </div>
              )}
            </div>

            <div style={badgeStyles.qrCompact}>
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="QR Code"
                  style={badgeStyles.qrSmall}
                />
              ) : (
                <div style={badgeStyles.qrPlaceholder}>...</div>
              )}

              {showAutorisation ? (
                <p style={badgeStyles.autorisationText}>
                  AUTORISATION<br />DE SORTIE
                </p>
              ) : (
                <p style={badgeStyles.qrLabel}></p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfigModal = ({ logoUrl, setLogoUrl, anneeScolaire, setAnneeScolaire, showConfig, setShowConfig }) => {
  const [tempUrl, setTempUrl] = useState(logoUrl);
  const [tempAnnee, setTempAnnee] = useState(anneeScolaire);

  const handleSave = () => {
    setLogoUrl(tempUrl);
    setAnneeScolaire(tempAnnee);
    localStorage.setItem('supemirLogo', tempUrl);
    localStorage.setItem('supemirAnneeScolaire', tempAnnee);
    setShowConfig(false);
  };

  if (!showConfig) return null;

  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <h3 style={modalStyles.title}>⚙️ Configuration SUPEMIR</h3>
        <div style={modalStyles.content}>
          <div style={modalStyles.section}>
            <label style={modalStyles.label}>URL du logo SUPEMIR:</label>
            <input
              type="text"
              value={tempUrl}
              onChange={(e) => setTempUrl(e.target.value)}
              placeholder="/images/logo-supemir.png"
              style={modalStyles.input}
            />
            {tempUrl && (
              <div style={modalStyles.preview}>
                <p style={modalStyles.previewLabel}>Aperçu:</p>
                <img
                  src={tempUrl}
                  alt="Logo"
                  style={modalStyles.previewImg}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <p style={{ ...modalStyles.previewLabel, display: 'none', color: '#dc2626' }}>
                  ❌ Logo introuvable
                </p>
              </div>
            )}
          </div>

          <div style={modalStyles.section}>
            <label style={modalStyles.label}>Année scolaire:</label>
            <input
              type="text"
              value={tempAnnee}
              onChange={(e) => setTempAnnee(e.target.value)}
              placeholder="2025-2026"
              style={modalStyles.input}
            />
            <p style={modalStyles.hint}>Format: 2025-2026</p>
          </div>
        </div>
        <div style={modalStyles.actions}>
          <button onClick={() => setShowConfig(false)} style={modalStyles.btnCancel}>
            Annuler
          </button>
          <button onClick={handleSave} style={modalStyles.btnSave}>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

const badgeStyles = {
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '20px',
  },
  card: {
    width: '450px',
    height: '280px',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    border: '2px solid #000000',
    transition: 'all 0.3s ease',
  },
  header: {
    height: '115px',
    background: 'linear-gradient(135deg, #0066cc 0%, #004c99 50%, #003366 100%)',
    padding: '18px 22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  logoContainer: {
    width: '85px',
    height: '85px',
    borderRadius: '14px',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    padding: '4px',
  },
  logo: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    borderRadius: '12px',
  },
  logoPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoText: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '800',
    letterSpacing: '1px',
  },
  headerText: {
    flex: 1,
    color: '#ffffff',
    textAlign: 'center',
  },
  cardType: {
    fontSize: '16px',
    fontWeight: '800',
    margin: '0 0 4px 0',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  cardSubtype: {
    fontSize: '10px',
    fontWeight: '600',
    margin: '0 0 4px 0',
    letterSpacing: '0.8px',
    opacity: 0.9,
    textTransform: 'uppercase',
  },
  cardYear: {
    fontSize: '11px',
    fontWeight: '700',
    margin: '0',
    letterSpacing: '0.8px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: '4px 12px',
    borderRadius: '12px',
    display: 'inline-block',
  },
  photoContainer: {
    width: '70px',
    height: '70px',
    borderRadius: '14px',
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    backdropFilter: 'blur(10px)',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  photoPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
    padding: '22px 24px 16px',
    display: 'flex',
    flexDirection: 'column',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '20px',
    height: '100%',
  },
  infoSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '4px',
  },
  infoRowName: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '4px',
    marginBottom: '2px',
  },
  infoRowMassar: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    background: 'linear-gradient(90deg, rgba(0, 102, 204, 0.1) 0%, rgba(0, 76, 153, 0.1) 100%)',
    padding: '8px 10px',
    margin: '0 -10px',
    borderRadius: '8px',
    borderLeft: '3px solid #0066cc',
  },
  label: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    minWidth: '110px',
  },
  valueName: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#000000',
    lineHeight: '1.3',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    textAlign: 'center',
    width: '100%',
  },
  valueHighlight: {
    fontSize: '16px',
    fontWeight: '800',
    color: '#000000',
    textAlign: 'center',
    width: '100%',
  },
  valueMassar: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#0066cc',
    fontFamily: "'Courier New', monospace",
    letterSpacing: '1.5px',
    whiteSpace: 'nowrap',
  },
  qrCompact: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '4px',
    paddingTop: '8px',
  },
  qrSmall: {
    width: '100px',
    height: '100px',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  qrPlaceholder: {
    width: '100px',
    height: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f1f5f9',
    borderRadius: '8px',
    color: '#64748b',
    fontSize: '12px',
    border: '2px solid #e5e7eb',
  },
  qrLabel: {
    margin: '0',
    fontSize: '10px',
    color: '#6b7280',
    fontWeight: '600',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  autorisationText: {
    margin: '0',
    fontSize: '8px',
    color: '#dc2626',
    fontWeight: '800',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: '1.2',
    padding: '3px 6px',
    backgroundColor: '#fee2e2',
    borderRadius: '4px',
    border: '1.5px solid #dc2626',
  },
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: 'linear-gradient(135deg, #0066cc 0%, #004c99 50%, #003366 100%)',
    padding: '24px 0',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  headerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 24px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#ffffff',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#ffffff',
    opacity: 0.9,
    margin: 0,
  },
  toolbar: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    padding: '20px 0',
  },
  toolbarContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 24px',
  },
  filtresRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  filtreGroupe: {
    flex: '1',
    minWidth: '200px',
  },
  inputRecherche: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  selectFiltre: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  btnConfig: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  btnSelect: {
    padding: '10px 20px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  btnDeselect: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '2px solid #fecaca',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  btnPrint: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 24px',
    background: 'linear-gradient(135deg, #0066cc 0%, #004c99 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0, 102, 204, 0.3)',
  },
  contentArea: {
    flex: 1,
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px 24px',
    width: '100%',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))',
    gap: '32px',
    justifyContent: 'center',
  },
  cardWrapper: {
    position: 'relative',
  },
  checkboxContainer: {
    position: 'absolute',
    top: '-12px',
    right: '-12px',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-end',
  },
  checkbox: {
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    padding: '4px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  btnAutorisation: {
    padding: '6px 12px',
    backgroundColor: '#ffffff',
    color: '#6b7280',
    border: '2px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
  },
  btnAutorisationActive: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    borderColor: '#dc2626',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: '16px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #0066cc',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '16px',
    color: '#64748b',
    fontWeight: '500',
    margin: 0,
  },
  btnRetry: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #0066cc 0%, #004c99 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: '16px',
  },
  emptyText: {
    fontSize: '18px',
    color: '#64748b',
    fontWeight: '500',
  },
  passwordContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0066cc 0%, #004c99 100%)',
  },
  passwordBox: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '48px 40px',
    width: '90%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
  },
  lockIcon: {
    fontSize: '64px',
    marginBottom: '24px',
  },
  passwordTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 12px 0',
  },
  passwordSubtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0 0 32px 0',
    lineHeight: '1.5',
  },
  passwordForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  passwordInput: {
    width: '100%',
    padding: '14px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '16px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
  },
  passwordButton: {
    width: '100%',
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #0066cc 0%, #004c99 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    boxShadow: '0 4px 12px rgba(0, 102, 204, 0.4)',
  },
  passwordErrorText: {
    color: '#dc2626',
    fontSize: '14px',
    fontWeight: '600',
    margin: '0',
  },
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 20px 0',
  },
  content: {
    marginBottom: '24px',
  },
  section: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  hint: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '4px',
    fontStyle: 'italic',
  },
  preview: {
    marginTop: '16px',
    textAlign: 'center',
  },
  previewLabel: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '8px',
  },
  previewImg: {
    width: '100px',
    height: '100px',
    objectFit: 'contain',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    padding: '8px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  btnCancel: {
    padding: '10px 20px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  btnSave: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #0066cc 0%, #004c99 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default BadgeGeneratorSupemir;